import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { isPersonLegal } from '@dealstack/shared/lib/common/business/person.ts';
import { zodResolver } from '@hookform/resolvers/zod';
import { useContext, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import {
	createPersonAddress,
	updatePersonAddress,
} from '@dealstack/frontend/app/services/Person.service.ts';
import { AddressTag } from '@dealstack/shared/lib/generated';
import { z } from 'zod';

const PERSON_LEGAL_ADDRESS_TAGS = [AddressTag.PRIMARY_ADDRESS, AddressTag.REGISTERED_ADDRESS];
const PERSON_NATURAL_ADDRESS_TAGS = [AddressTag.PRIMARY_ADDRESS];

export enum ADDRESS_FORM_FIELDS {
	tags = 'tags',
	country = 'country',
	postCode = 'postCode',
	street = 'street',
	city = 'city',
	houseNumber = 'houseNumber',
}

const AddressShape = z.object({
	tags: z.array(z.nativeEnum(AddressTag)).optional(),
	street: z.string().nullish(),
	city: z.string().nullish(),
	state: z.string().nullish(),
	country: z.string().nullish(),
	postCode: z.string().nullish(),
	houseNumber: z.string().nullish(),
});
type PersonAddressDto = z.infer<typeof AddressShape>;

export const useAddressController = () => {
	const tCapManage = useTranslationCapManage();

	const createAddressMutation = useMutation({
		mutationFn: createPersonAddress,
	});

	const updateAddressMutation = useMutation({
		mutationFn: updatePersonAddress,
	});

	const { selectedEntityId, selectedEntityData, refetchSelectedEntity } = useContext(
		CapManageControllerContext
	);

	const addressForm = useForm<PersonAddressDto>({
		mode: 'all',
		resolver: zodResolver(AddressShape),
		defaultValues: { [ADDRESS_FORM_FIELDS.tags]: [] },
	});

	const isAllFieldsEmpty = useMemo(() => {
		const formValues = addressForm.getValues();
		return Object.values(formValues).every((fieldValue) => !fieldValue);
	}, [addressForm.watch()]);

	const onFormSubmit = async (formValues: PersonAddressDto, addressId?: string) => {
		await updateEntityAddressDetails(formValues, addressId);
	};

	const updateEntityAddressDetails = async (formValues, addressId?: string) => {
		if (!selectedEntityData) {
			return;
		}

		try {
			if (addressId) {
				await updateAddressMutation.mutateAsync({
					personId: selectedEntityId ?? '',
					updateArgs: {
						...formValues,
						id: addressId,
					},
				});
			} else {
				await createAddressMutation.mutateAsync({
					personId: selectedEntityId ?? '',
					createArgs: formValues,
				});
			}

			await refetchSelectedEntity();
			showToastV2({ state: 'success', message: 'Address update successful' });
		} catch {
			showToastV2({ state: 'error', message: 'Failed to update address' });
		}
	};

	const tagsOptions = useMemo(() => {
		if (!selectedEntityData) {
			return [];
		}
		const tagsVariants = isPersonLegal(selectedEntityData.type)
			? PERSON_LEGAL_ADDRESS_TAGS
			: PERSON_NATURAL_ADDRESS_TAGS;

		return tagsVariants.map((tag) => ({
			value: tag,
			label: tCapManage(`basic_details.address_form.tags.${tag}`),
		}));
	}, [selectedEntityData]);

	return {
		onFormSubmit,
		addressForm,
		control: addressForm.control,
		isAllFieldsEmpty,
		selectedEntityData,
		tagsOptions,
		requestLoading: createAddressMutation.isPending || updateAddressMutation.isPending,
	};
};
